import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useBootStore } from "@/stores/bootStore";

export function BootScreen() {
  const phase = useBootStore((s) => s.phase);
  const loadProgress = useBootStore((s) => s.loadProgress);
  const lastError = useBootStore((s) => s.lastError);
  const reset = useBootStore((s) => s.reset);

  const visible = phase === "parsing" || phase === "error";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="boot-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="fixed inset-0 flex flex-col items-center justify-center"
          style={{
            zIndex: 200,
            backgroundColor: "var(--bg-base)",
          }}
        >
          {/* Logo */}
          <motion.img
            src="/lytick-icons/mobile-white.svg"
            alt="Lytick"
            className="w-14 h-14 select-none mb-8"
            draggable={false}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          />

          {phase === "parsing" ? (
            /* ── CSV parsing progress ── */
            <div className="flex flex-col items-center gap-4">
              <p
                className="text-sm font-medium"
                style={{ color: "var(--text-secondary)" }}
              >
                Parsing CSV…
              </p>
              <div className="w-44 h-[3px] rounded-full overflow-hidden relative" style={{ backgroundColor: "var(--bg-overlay)" }}>
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ backgroundColor: "var(--lyt-accent)" }}
                  initial={{ width: "0%" }}
                  animate={{ width: `${loadProgress}%` }}
                  transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                />
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ backgroundColor: "rgba(14,196,180,0.35)", width: "30%" }}
                  initial={{ x: "-30%" }}
                  animate={{ x: "130%" }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
            </div>
          ) : (
            /* ── Error state ── */
            <div className="flex flex-col items-center gap-4 max-w-xs text-center">
              <div
                className="size-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "color-mix(in srgb, var(--color-loss) 15%, transparent)" }}
              >
                <AlertCircle className="size-5 text-loss" />
              </div>
              <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                Import failed
              </p>
              {lastError && (
                <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {lastError}
                </p>
              )}
              <button
                type="button"
                onClick={reset}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-transform hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  backgroundColor: "var(--bg-overlay)",
                  color: "var(--text-primary)",
                }}
              >
                <RefreshCw className="size-3" />
                Dismiss
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
